import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Mail, Palette, Eye, Code, RotateCcw, Save, ChevronRight,
  CheckCircle2, AlertCircle, Loader2, Copy, Check, X, Info,
} from 'lucide-react';
import { emailTemplatesAPI } from '../../services/api';
import { useLocale } from '../../contexts/LocaleContext';

// ── Template type meta ────────────────────────────────────────────────────────
const TYPE_META = {
  deposit_approved:    { labelKey: 'emailTemplates.typeDepositApproved',    emoji: '✅', color: 'text-[var(--accent-success)] bg-[var(--accent-success)]/10' },
  deposit_rejected:    { labelKey: 'emailTemplates.typeDepositRejected',    emoji: '❌', color: 'text-[var(--accent-danger)] bg-[var(--accent-danger)]/10' },
  withdrawal_approved: { labelKey: 'emailTemplates.typeWithdrawalApproved', emoji: '💸', color: 'text-[var(--accent-success)] bg-[var(--accent-success)]/10' },
  withdrawal_rejected: { labelKey: 'emailTemplates.typeWithdrawalRejected', emoji: '⚠️', color: 'text-[var(--accent-warning)] bg-[var(--accent-warning)]/10' },
  margin_call:         { labelKey: 'emailTemplates.typeMarginCall',         emoji: '⚠️', color: 'text-[var(--accent-warning)] bg-[var(--accent-warning)]/10' },
  liquidation:         { labelKey: 'emailTemplates.typeLiquidation',        emoji: '🚨', color: 'text-[var(--accent-danger)] bg-[var(--accent-danger)]/10' },
  kyc_approved:        { labelKey: 'emailTemplates.typeKycApproved',        emoji: '🎉', color: 'text-[var(--accent-success)] bg-[var(--accent-success)]/10' },
  kyc_rejected:        { labelKey: 'emailTemplates.typeKycRejected',        emoji: '❌', color: 'text-[var(--accent-danger)] bg-[var(--accent-danger)]/10' },
  welcome:             { labelKey: 'emailTemplates.typeWelcome',            emoji: '👋', color: 'text-[var(--accent-primary)] bg-[var(--accent-primary)]/10' },
};

const fieldCls = "px-3 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg text-xs focus:border-[var(--accent-primary)] focus:outline-none text-[var(--text-primary)] placeholder-[var(--text-tertiary)] w-full";

// ── Copy button ───────────────────────────────────────────────────────────────
function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={copy} className="p-0.5 rounded text-[var(--text-tertiary)] hover:text-[var(--accent-primary)] transition-smooth">
      {copied ? <Check className="w-3 h-3 text-[var(--accent-success)]" /> : <Copy className="w-3 h-3" />}
    </button>
  );
}

// ── Branding panel ────────────────────────────────────────────────────────────
function BrandingPanel({ onClose }) {
  const { t } = useLocale();
  const [branding, setBranding] = useState(null);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    emailTemplatesAPI.getBranding().then(d => setBranding(d)).finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-xs text-[var(--text-secondary)] py-2">{t('emailTemplates.loadingBranding')}</p>;

  return (
    <div className="border border-[var(--border-color)] rounded-lg p-3 bg-[var(--bg-secondary)] space-y-2">
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs font-semibold text-[var(--text-primary)] flex items-center gap-1.5">
          <Palette className="w-3.5 h-3.5 text-[var(--accent-primary)]" />
          {t('emailTemplates.activeBranding')}
        </p>
        <button onClick={onClose} className="p-0.5 rounded text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-smooth">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] text-[var(--text-tertiary)] uppercase tracking-wider block mb-1">{t('emailTemplates.companyName')}</label>
          <p className="text-xs font-medium text-[var(--text-primary)]">{branding?.company_name || '—'}</p>
        </div>
        <div>
          <label className="text-[10px] text-[var(--text-tertiary)] uppercase tracking-wider block mb-1">{t('emailTemplates.primaryColor')}</label>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded border border-[var(--border-color)] shrink-0"
              style={{ backgroundColor: branding?.primary_color || '#1976D2' }} />
            <p className="text-xs font-mono text-[var(--text-primary)]">{branding?.primary_color || '#1976D2'}</p>
          </div>
        </div>
        <div className="col-span-1 sm:col-span-2">
          <label className="text-[10px] text-[var(--text-tertiary)] uppercase tracking-wider block mb-1">{t('emailTemplates.logoUrl')}</label>
          <p className="text-xs text-[var(--text-secondary)] truncate">{branding?.logo_url || t('emailTemplates.noLogoSet')}</p>
        </div>
        <div className="col-span-1 sm:col-span-2">
          <label className="text-[10px] text-[var(--text-tertiary)] uppercase tracking-wider block mb-1">{t('emailTemplates.emailFooter')}</label>
          <p className="text-xs text-[var(--text-secondary)]">{branding?.footer_text || '—'}</p>
        </div>
      </div>

      <div className="flex gap-2 items-start p-2 rounded-lg bg-[var(--accent-primary)]/5 border border-[var(--accent-primary)]/15 mt-1">
        <Info className="w-3.5 h-3.5 text-[var(--accent-primary)] shrink-0 mt-0.5" />
        <p className="text-xs text-[var(--text-secondary)]">{t('emailTemplates.brandingNote')}</p>
      </div>
    </div>
  );
}

// ── Template editor ───────────────────────────────────────────────────────────
function TemplateEditor({ template, onSave, onReset, onBack }) {
  const { t } = useLocale();
  const [subject,      setSubject]      = useState(template.subject);
  const [htmlBody,     setHtmlBody]     = useState(template.html_body);
  const [viewMode,     setViewMode]     = useState('wysiwyg');
  const [saving,       setSaving]       = useState(false);
  const [resetting,    setResetting]    = useState(false);
  const [error,        setError]        = useState(null);
  const [showAllVars,  setShowAllVars]  = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    if (viewMode === 'preview' && iframeRef.current) {
      const doc = iframeRef.current.contentDocument;
      doc.open(); doc.write(htmlBody); doc.close();
    }
  }, [htmlBody, viewMode]);

  const variables = template.variables || [];

  const insertVar = (v) => { if (viewMode === 'html') setHtmlBody(b => b + `{{${v}}}`); };

  const handleSave = async () => {
    if (!subject.trim() || !htmlBody.trim()) return setError(t('emailTemplates.subjectBodyRequired'));
    setSaving(true); setError(null);
    try { await onSave(subject, htmlBody); }
    catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!window.confirm(t('emailTemplates.resetConfirm'))) return;
    setResetting(true);
    try { await onReset(); onBack(); }
    finally { setResetting(false); }
  };

  const meta = TYPE_META[template.type] || {};

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={onBack} className="text-xs text-[var(--accent-primary)] hover:underline transition-smooth flex items-center gap-1">
          {t('emailTemplates.allTemplates')}
        </button>
        <span className="text-[var(--text-tertiary)] text-xs">/</span>
        <span className="text-xs font-semibold text-[var(--text-primary)] flex items-center gap-1.5">
          <span>{meta.emoji}</span>
          <span className="hidden sm:inline">{meta.labelKey ? t(meta.labelKey) : template.type}</span>
        </span>
        {template.is_custom && (
          <span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-[var(--accent-primary)] bg-[var(--accent-primary)]/10 px-2 py-0.5 rounded-full">
            {t('emailTemplates.customised')}
          </span>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 text-xs text-[var(--accent-danger)] p-2.5 rounded-lg bg-[var(--accent-danger)]/8 border border-[var(--accent-danger)]/20">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />{error}
        </div>
      )}

      {/* Subject */}
      <div>
        <label className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider block mb-1">{t('emailTemplates.subjectLine')}</label>
        <input className={fieldCls} value={subject} onChange={e => setSubject(e.target.value)} placeholder={t('emailTemplates.subjectPlaceholder')} />
      </div>

      {/* View mode tabs */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex gap-1 p-1 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg w-full sm:w-auto overflow-x-auto">
          {[
            { id: 'wysiwyg', labelKey: 'emailTemplates.editorTab', icon: Mail },
            { id: 'html',    labelKey: 'emailTemplates.htmlTab',   icon: Code },
            { id: 'preview', labelKey: 'emailTemplates.previewTab',icon: Eye  },
          ].map(({ id, labelKey, icon: Icon }) => (
            <button key={id} onClick={() => setViewMode(id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold transition-smooth whitespace-nowrap ${
                viewMode === id ? 'bg-[var(--accent-primary)] text-white' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              }`}>
              <Icon className="w-3 h-3" />{t(labelKey)}
            </button>
          ))}
        </div>

        {variables.length > 0 && (
          <div className="w-full sm:w-auto">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[10px] text-[var(--text-tertiary)] font-semibold">{t('emailTemplates.variables')}</span>
              {variables.slice(0, window.innerWidth < 640 ? 3 : 5).map(v => (
                <span key={v} className="inline-flex items-center gap-0.5 font-mono text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--accent-primary)]">
                  {`{{${v}}}`}<CopyBtn text={`{{${v}}}`} />
                </span>
              ))}
              {variables.length > (window.innerWidth < 640 ? 3 : 5) && (
                <button onClick={() => setShowAllVars(!showAllVars)}
                  className="text-[10px] text-[var(--accent-primary)] hover:underline font-medium">
                  {showAllVars ? t('emailTemplates.showLess') : `+${variables.length - (window.innerWidth < 640 ? 3 : 5)} more`}
                </button>
              )}
            </div>
            {showAllVars && (
              <div className="mt-2 p-2 rounded-lg bg-[var(--bg-tertiary)] border border-[var(--border-color)]">
                <div className="flex flex-wrap gap-1.5">
                  {variables.map(v => (
                    <button key={v} onClick={() => insertVar(v)}
                      className="inline-flex items-center gap-0.5 font-mono text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--accent-primary)] hover:bg-[var(--accent-primary)]/10 transition-smooth">
                      {`{{${v}}}`}<CopyBtn text={`{{${v}}}`} />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Editor area */}
      <div className="border border-[var(--border-color)] rounded-lg overflow-hidden">
        {viewMode === 'wysiwyg' && (
          <div contentEditable suppressContentEditableWarning
            onInput={e => setHtmlBody(e.currentTarget.innerHTML)}
            dangerouslySetInnerHTML={{ __html: htmlBody }}
            className="min-h-[320px] p-3 sm:p-4 text-sm text-[var(--text-primary)] bg-white focus:outline-none overflow-auto"
            style={{ color: '#1a202c' }} />
        )}
        {viewMode === 'html' && (
          <textarea className="w-full min-h-[320px] p-3 sm:p-4 text-xs font-mono bg-[var(--bg-tertiary)] text-[var(--text-primary)] resize-y focus:outline-none"
            value={htmlBody} onChange={e => setHtmlBody(e.target.value)} spellCheck={false} />
        )}
        {viewMode === 'preview' && (
          <iframe ref={iframeRef} title="Email preview" className="w-full border-0"
            style={{ minHeight: 400, background: '#fff' }} sandbox="allow-same-origin" />
        )}
      </div>

      {/* HTML mode variable hint */}
      {viewMode === 'html' && variables.length > 0 && (
        <div className="flex gap-2 items-start p-2.5 rounded-lg bg-[var(--accent-primary)]/5 border border-[var(--accent-primary)]/15">
          <Info className="w-3.5 h-3.5 text-[var(--accent-primary)] shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-[10px] text-[var(--text-secondary)] mb-2">{t('emailTemplates.clickToInsert')}</p>
            <div className="flex flex-wrap gap-1.5">
              {variables.map(v => (
                <button key={v} onClick={() => insertVar(v)}
                  className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--accent-primary)] hover:bg-[var(--accent-primary)]/10 transition-smooth">
                  {`{{${v}}}`}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-1">
        <button onClick={handleSave} disabled={saving}
          className="flex items-center justify-center gap-1.5 px-4 py-2 bg-[var(--accent-primary)] text-white rounded-lg text-xs font-semibold disabled:opacity-40 transition-smooth">
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
          {saving ? t('emailTemplates.savingTemplate') : t('emailTemplates.saveTemplate')}
        </button>
        {template.is_custom && (
          <button onClick={handleReset} disabled={resetting}
            className="flex items-center justify-center gap-1.5 px-4 py-2 border border-[var(--border-color)] text-[var(--text-secondary)] rounded-lg text-xs hover:bg-[var(--bg-secondary)] disabled:opacity-40 transition-smooth">
            {resetting ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
            {resetting ? t('emailTemplates.resetting') : t('emailTemplates.resetToDefault')}
          </button>
        )}
        <button onClick={onBack}
          className="px-4 py-2 border border-[var(--border-color)] rounded-lg text-xs text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] transition-smooth sm:ml-auto">
          {t('emailTemplates.back')}
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Main panel
// ══════════════════════════════════════════════════════════════════════════════
export default function EmailTemplatesPanel() {
  const { t } = useLocale();
  const [templates,       setTemplates]       = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState(null);
  const [editingType,     setEditingType]     = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [loadingEdit,     setLoadingEdit]     = useState(false);
  const [showBranding,    setShowBranding]    = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await emailTemplatesAPI.getAll();
      setTemplates(data?.templates || data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message || t('emailTemplates.loadingTemplates'));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEditor = async (type) => {
    setLoadingEdit(true); setEditingType(type);
    try {
      const data = await emailTemplatesAPI.getOne(type);
      setEditingTemplate(data?.template || data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setEditingType(null);
    } finally { setLoadingEdit(false); }
  };

  const handleSave  = async (subject, htmlBody) => { await emailTemplatesAPI.save(editingType, { subject, html_body: htmlBody }); await load(); };
  const handleReset = async () => { await emailTemplatesAPI.reset(editingType); await load(); };
  const handleBack  = () => { setEditingType(null); setEditingTemplate(null); };

  if (loading) return <p className="text-xs text-[var(--text-secondary)] py-2">{t('emailTemplates.loadingTemplates')}</p>;

  if (error) return (
    <div className="flex items-center gap-2 text-xs text-[var(--accent-danger)] py-2">
      {error} <button onClick={load} className="underline">{t('emailTemplates.retry')}</button>
    </div>
  );

  if (editingType) {
    if (loadingEdit) return <p className="text-xs text-[var(--text-secondary)] py-2">{t('emailTemplates.loadingTemplate')}</p>;
    if (editingTemplate) {
      return <TemplateEditor template={editingTemplate} onSave={handleSave} onReset={handleReset} onBack={handleBack} />;
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        <p className="text-xs text-[var(--text-secondary)]">{t('emailTemplates.templateListDesc')}</p>
        <button onClick={() => setShowBranding(b => !b)}
          className="flex items-center gap-1.5 text-xs text-[var(--accent-primary)] hover:text-[var(--accent-primary-dark)] transition-smooth whitespace-nowrap">
          <Palette className="w-3.5 h-3.5" />
          {showBranding ? t('emailTemplates.closeBranding') : t('emailTemplates.editBranding')}
        </button>
      </div>

      {showBranding && <BrandingPanel onClose={() => setShowBranding(false)} />}

      <div className="space-y-1.5">
        {templates.map(t_item => {
          const meta = TYPE_META[t_item.type] || {};
          return (
            <button key={t_item.type} onClick={() => openEditor(t_item.type)}
              className="w-full flex items-center gap-2 sm:gap-3 px-3 py-2.5 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg hover:border-[var(--accent-primary)] transition-smooth text-left group">
              <span className="text-base sm:text-lg shrink-0">{meta.emoji || '✉️'}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  <p className="text-xs sm:text-sm font-medium text-[var(--text-primary)] truncate">
                    {meta.labelKey ? t(meta.labelKey) : t_item.type}
                  </p>
                  {t_item.is_custom
                    ? <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--accent-primary)] bg-[var(--accent-primary)]/10 px-1.5 py-0.5 rounded-full shrink-0">{t('emailTemplates.custom')}</span>
                    : <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-tertiary)] bg-[var(--text-tertiary)]/10 px-1.5 py-0.5 rounded-full shrink-0">{t('emailTemplates.default')}</span>
                  }
                </div>
                <p className="text-[11px] sm:text-xs text-[var(--text-secondary)] truncate">{t_item.subject}</p>
              </div>
              {t_item.is_custom
                ? <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[var(--accent-success)] shrink-0" />
                : <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[var(--text-tertiary)] shrink-0 group-hover:text-[var(--accent-primary)] transition-smooth" />
              }
            </button>
          );
        })}
      </div>

      <div className="flex gap-2 items-start p-2.5 rounded-lg bg-[var(--accent-primary)]/5 border border-[var(--accent-primary)]/15">
        <Info className="w-3.5 h-3.5 text-[var(--accent-primary)] shrink-0 mt-0.5" />
        <p className="text-xs text-[var(--text-secondary)] leading-relaxed">{t('emailTemplates.templateInfoNote')}</p>
      </div>
    </div>
  );
}